/**
 * @fileoverview Syndicator Showdown — Public Stable Share Page (Phase 6)
 * @module pages/ShareStablePage
 *
 * Public route: /share/stable/:shareId
 * No authentication required. Renders only the privacy-safe snapshot stored
 * on stable_shares/{shareId}, plus the (also public) game_slates leaderboard
 * subcollection for a LIVE rank/virtual-winnings figure — never reads
 * player_entries directly.
 *
 * Runner-level selections are shown only when the share's revealMode allows
 * it: either the owner explicitly chose "immediate", or the associated
 * showdown's entries have since closed (see utils/revealRules.js).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { fetchStableShare } from '../services/stableShareService';
import { getValueBadgeMeta } from '../utils/valueBadge';
import { DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import { getShowdownDisplayState } from '../utils/showdownStatus';
import { canRevealSelections, buildRevealTeaserText } from '../utils/revealRules';
import { buildChallengeText } from '../utils/shareCopy';
import { trackShareView, trackShareEvent, storeShareAttribution } from '../services/shareTrackingService';
import ShareStableCard from '../components/share/ShareStableCard';
import './ShareStablePage.css';

function fmt$(n) {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

const ValuePill = ({ valueBadge }) => {
  if (!valueBadge || valueBadge === 'UNKNOWN') return null;
  const meta = getValueBadgeMeta(valueBadge);
  return (
    <span
      className="shs-value-pill"
      style={{ borderColor: meta.colour, color: meta.colour, background: meta.bg }}
    >
      {meta.label}
    </span>
  );
};

/** Best-effort SEO — updates the tab title + meta description for this page only. */
function useShareSeo(share) {
  useEffect(() => {
    if (!share) return undefined;
    const prevTitle = document.title;
    const name = share.ownerDisplayName || 'A Syndicator Showdown player';
    const showdown = share.snapshot?.gameName || 'a Showdown';
    document.title = `${name}’s ${showdown} Stable | Syndicator Showdown`;

    let metaTag = document.querySelector('meta[name="description"]');
    const prevContent = metaTag?.getAttribute('content') || null;
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', `See ${name}'s stable progress and join Syndicator Showdown.`);

    return () => {
      document.title = prevTitle;
      if (metaTag && prevContent !== null) metaTag.setAttribute('content', prevContent);
    };
  }, [share]);
}

export function ShareStablePage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [share, setShare]     = useState(undefined); // undefined = loading, null = not found
  const [displayState, setDisplayState] = useState(null);
  const [leaderboardRow, setLeaderboardRow] = useState(null);
  const [currentUser, setCurrentUser] = useState(undefined); // undefined = auth not yet resolved

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => onAuthStateChanged(auth, setCurrentUser), []);

  useEffect(() => {
    if (!shareId) { setShare(null); return; }

    let cancelled = false;
    fetchStableShare(shareId)
      .then(async (result) => {
        if (cancelled) return;
        if (!result || result.visibility !== 'public') { setShare(null); return; }
        setShare(result);
        trackShareView(shareId);

        if (result.slateId) {
          try {
            const slateSnap = await getDoc(doc(db, 'game_slates', result.slateId));
            if (!cancelled && slateSnap.exists()) {
              setDisplayState(getShowdownDisplayState({ id: slateSnap.id, ...slateSnap.data() }));
            }
          } catch {
            // Non-fatal — falls back to whatever revealMode already says
          }

          try {
            const lbSnap = await getDoc(doc(db, 'game_slates', result.slateId, 'leaderboard', result.ownerUid));
            if (!cancelled && lbSnap.exists()) setLeaderboardRow(lbSnap.data());
          } catch {
            // Non-fatal — the card just omits a live rank/winnings figure
          }
        }
      })
      .catch((err) => {
        console.error('[ShareStablePage] fetchStableShare error:', err);
        if (!cancelled) setShare(null);
      });

    return () => { cancelled = true; };
  }, [shareId]);

  useShareSeo(share);

  const entriesClosed = !!displayState && (displayState.isResultsPending || displayState.isCompleted);
  const canReveal = canRevealSelections({ revealMode: share?.revealMode, entriesClosed });

  const handleCta = useCallback((eventType) => {
    if (shareId) trackShareEvent(shareId, eventType);
    if (share?.shareId || shareId) {
      storeShareAttribution({ shareId: shareId, ownerUid: share?.ownerUid });
    }

    if (!currentUser) {
      navigate('/signin?mode=signup');
      return;
    }
    if (displayState?.isEnterable && share?.slateId) {
      navigate(`/slate/${share.slateId}`);
    } else {
      navigate('/showdowns');
    }
  }, [shareId, share, currentUser, displayState, navigate]);

  const handleViewLeaderboard = useCallback(() => {
    if (share?.slateId) navigate(`/leaderboard/${share.slateId}`);
  }, [share, navigate]);

  const challenge = buildChallengeText({
    stableName: share?.ownerDisplayName,
    showdownName: share?.snapshot?.gameName,
  });

  return (
    <div className="shs-root">
      <div className="shs-bg" />

      <header className="shs-header">
        <Link to="/" className="shs-logo-link">
          <img src="/logo.png" alt="Syndicator Showdown" className="shs-logo" />
        </Link>
        <Link to="/signin" className="shs-btn-ghost">Login</Link>
      </header>

      <main className="shs-content">
        {share === undefined ? (
          <div className="shs-spinner-wrap"><div className="ss-spinner" /></div>
        ) : share === null ? (
          <div className="shs-card shs-card--empty">
            <span className="shs-eyebrow">🐎 SHARED STABLE</span>
            <h1 className="shs-title">This share link is no longer available</h1>
            <p className="shs-subtitle">
              It may have been removed, or the link has expired.
            </p>
            <div className="shs-actions">
              <Link to="/showdowns" className="shs-btn-primary">Explore Showdowns →</Link>
              <Link to="/" className="shs-btn-secondary">Back to Home</Link>
            </div>
          </div>
        ) : (
          <>
            <ShareStableCard
              avatarUrl={share.ownerPhotoURL || DEFAULT_PROFILE_IMAGE}
              stableName={share.ownerDisplayName}
              showdownName={share.snapshot?.gameName}
              statusLabel={displayState?.statusLabel}
              resultedRaceCount={displayState?.resultedRaceCount ?? 0}
              totalRaceCount={displayState?.totalRaceCount ?? (share.snapshot?.races?.length || 0)}
              virtualWinnings={leaderboardRow?.totalEarnings ?? 0}
              rank={leaderboardRow?.position ?? null}
              totalRunners={share.snapshot?.totalRunners ?? 0}
              actions={(
                <>
                  <button type="button" className="shs-btn-primary" onClick={() => handleCta('challenge_click')}>
                    Join the Showdown →
                  </button>
                  <button type="button" className="shs-btn-secondary" onClick={() => handleCta('challenge_click')}>
                    Create Your Stable
                  </button>
                  {share.slateId && (
                    <button type="button" className="shs-btn-secondary" onClick={handleViewLeaderboard}>
                      View Leaderboard
                    </button>
                  )}
                </>
              )}
            />

            <div className="shs-challenge-card">
              <span className="shs-eyebrow">🏇 CHALLENGE A FRIEND</span>
              <h2 className="shs-challenge-headline">{challenge.headline}</h2>
              <p className="shs-challenge-body">{challenge.body}</p>
              <div className="shs-actions">
                <button type="button" className="shs-btn-primary" onClick={() => handleCta('challenge_click')}>
                  Challenge This Stable
                </button>
              </div>
            </div>

            <div className="shs-races">
              {(share.snapshot?.races || []).map((race) => (
                <div key={race.raceId} className="shs-race">
                  <div className="shs-race-header">
                    <span className="shs-race-leg">
                      {race.raceNumber ? `Leg ${race.raceNumber}` : 'Race'}
                    </span>
                    <span className="shs-race-name">
                      {race.raceName || race.raceId}
                      {race.meetingName ? ` · ${race.meetingName}` : ''}
                    </span>
                    <span className="shs-race-spend">{fmt$(race.totalSpentOnRace)}</span>
                  </div>

                  {canReveal ? (
                    <div className="shs-runners">
                      {race.selectedRunners.map((r) => (
                        <div key={`${race.raceId}_${r.runnerName}`} className="shs-runner-row">
                          <div className="shs-runner-left">
                            {r.runnerNumber != null && (
                              <span className="shs-runner-num">{r.runnerNumber}</span>
                            )}
                            <span className="shs-runner-name">{r.runnerName}</span>
                            {r.displayRating != null && (
                              <span className="shs-runner-rating">{Math.round(r.displayRating)}</span>
                            )}
                            <ValuePill valueBadge={r.valueBadge} />
                          </div>
                          <div className="shs-runner-right">
                            <span className="shs-runner-equity">{(r.equityPercent || 0).toFixed(0)}%</span>
                            <span className="shs-runner-cost">{fmt$(r.purchaseCost)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="shs-hidden-note">
                      🔒 {race.selectedRunners.length} runner
                      {race.selectedRunners.length === 1 ? '' : 's'} selected — hidden until entries close
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!canReveal && (
              <p className="shs-reveal-footnote">
                {buildRevealTeaserText(share.ownerDisplayName)}
                {' '}
                <button type="button" className="shs-reveal-cta" onClick={() => handleCta('challenge_click')}>
                  Build your own stable →
                </button>
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default ShareStablePage;
